import { useState } from "react";
import SvgMapper from "../../../features/common/SvgMapper";
import "../../Style.css";
import { SWAGGER_URL } from "../../../../common/apiBaseUrl";

const CustomQuestions = ({ onChange }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [rubricFile, setRubricFile] = useState(null);
  const [rubricError, setRubricError] = useState("");
  const [fileError, setFileError] = useState("");

  const handleRemoveFile = (fileName) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
    setFileError("");
  };

  const handleRemoveRubric = () => {
    setRubricFile(null);
    setRubricError("");
    onChange({
      target: { name: "rubric_url", value: "" },
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const invalidFiles = files.filter(
      (file) => !file.name.toLowerCase().endsWith(".xlsx")
    );

    if (invalidFiles.length > 0) {
      setFileError("Only .xlsx files are allowed for custom questions upload");
      e.target.value = "";
      setSelectedFiles([]);
      return;
    }

    setFileError("");
    setSelectedFiles(files);

    try {
      let uploadedUrl = "";

      for (const file of files) {
        const fileUrl = await uploadToS3(file);
        if (fileUrl) uploadedUrl = fileUrl;
      }

      onChange({
        target: { name: "additionalData", value: uploadedUrl },
      });
    } catch (error) {
      console.error("Upload error:", error);
      setFileError("Failed to upload file. Please try again.");
      setSelectedFiles([]);
    }
  };

  const handleRubricChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setRubricError("Only .xlsx files are allowed for rubric upload");
      e.target.value = "";
      return;
    }

    setRubricError("");
    setRubricFile(file);

    try {
      const url = await uploadToS3(file);
      if (url) {
        onChange({
          target: { name: "rubric_url", value: url },
        });
      }
    } catch (error) {
      console.error("Rubric upload error:", error);
      setRubricError("Failed to upload rubric file. Please try again.");
      setRubricFile(null);
    }
  };

  const uploadToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", "ai-bot");
      formData.append("file", file);

      const response = await fetch(
        `${SWAGGER_URL}/storage/add-external`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("File upload failed");
      const data = await response.json();
      return data?.url || data?.fileUrl || null;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      return null;
    }
  };

  return (
    <div className="custom-questions-section">
      <div className="custom-questions-header">
        <div className="custom-questions-number-badge-blue">
          3
        </div>
        <h3 className="custom-questions-title">
          Custom Questions
        </h3>
        <span className="custom-questions-subtitle">
          (Optional - Upload Google Sheets)
        </span>
      </div>

      <div className="custom-questions-upload-area-blue">
        <div className="custom-questions-upload-content">
          <div className="custom-questions-icon-container">
            {SvgMapper.spreadSheet("w-12 h-12 text-gray-400")}
          </div>

          <div>
            <h4 className="custom-questions-upload-heading">
              Upload Custom Questions
            </h4>
            <p className="custom-questions-upload-description">
              Upload a .xlsx file with custom questions for this interview
            </p>

            <div className="custom-questions-badge-container">
              <span className="custom-questions-badge">
                .xlsx
              </span>
            </div>

            <input
              type="file"
              id="custom-questions-upload"
              accept=".xlsx"
              className="custom-questions-file-input"
              onChange={handleFileChange}
            />

            <label
              htmlFor="custom-questions-upload"
              className="custom-questions-file-button"
            >
              {SvgMapper.fileIcon("w-4 h-4 mr-2")}
              Choose File
            </label>
            {fileError && (
              <p className="custom-questions-error">{fileError}</p>
            )}
          </div>
        </div>
      </div>

      {selectedFiles?.length > 0 &&
        selectedFiles?.map((file) => (
          <div
            key={file.name}
            className="custom-questions-file-item"
          >
            <div className="custom-questions-file-item-left">
              <div className="custom-questions-file-icon-green">
                {SvgMapper.spreadSheet("w-6 h-6 text-green-600")}
              </div>
              <div>
                <p className="custom-questions-file-name">
                  {file?.name}
                </p>
                <p className="custom-questions-file-size">
                  {formatFileSize(file?.size)}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleRemoveFile(file.name)}
              className="custom-questions-remove-button"
              title="Remove file"
            >
              ✕
            </button>
          </div>
        ))}

      <div className="custom-questions-rubric-header">
        <div className="custom-questions-number-badge-purple">
          4
        </div>
        <h3 className="custom-questions-title">
          Upload Rubric <span className="custom-questions-asterisk">*</span>
        </h3>
      </div>

      <div className="custom-questions-upload-area-purple">
        <div className="custom-questions-upload-content">
          <div className="custom-questions-icon-container">
            {SvgMapper.spreadSheet("w-12 h-12 text-gray-400")}
          </div>

          <div>
            <h4 className="custom-questions-upload-heading">
              Upload Evaluation Rubric
            </h4>
            <p className="custom-questions-upload-description">
              Upload a .xlsx file containing the scoring rubric for this interview
            </p>

            <div className="custom-questions-badge-container">
              <span className="custom-questions-badge">
                .xlsx
              </span>
            </div>

            <input
              type="file"
              id="rubric-upload"
              accept=".xlsx"
              className="custom-questions-file-input"
              onChange={handleRubricChange}
            />

            <label
              htmlFor="rubric-upload"
              className="custom-questions-file-button"
            >
              {SvgMapper.fileIcon("w-4 h-4 mr-2")}
              Choose Rubric File
            </label>
            {rubricError && (
              <p className="custom-questions-error">{rubricError}</p>
            )}
          </div>
        </div>
      </div>

      {rubricFile && (
        <div className="custom-questions-file-item">
          <div className="custom-questions-file-item-left">
            <div className="custom-questions-file-icon-blue">
              {SvgMapper.spreadSheet("w-6 h-6 text-blue-600")}
            </div>
            <div>
              <p className="custom-questions-file-name">
                {rubricFile.name}
              </p>
              <p className="custom-questions-file-size">
                {formatFileSize(rubricFile.size)}
              </p>
            </div>
          </div>
          <button
            onClick={handleRemoveRubric}
            className="custom-questions-remove-button"
            title="Remove rubric file"
          >
            ✕
          </button>
        </div>
      )}

      <div className="custom-questions-info-box">
        <h4 className="custom-questions-info-heading">Format</h4>
        <div className="custom-questions-info-text">
         
          
          <a
            href="https://s3.ap-south-1.amazonaws.com/testconvexstorage.kraftshala.com/test/no-auth-uplaod//14i86yulejmjbqbg3b.xlsx"
            target="_blank"
            rel="noopener noreferrer"
            className="custom-questions-info-link"
          >
            Download rubric Format Template
          </a>
        </div>
      </div>
    </div>
  );
};

export default CustomQuestions;
